import React, { useEffect } from 'react';
import { BrowserRouter, useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { QueryClientProvider } from '@tanstack/react-query';
import AppErrorBoundary from '../shared/components/ui/AppErrorBoundary';
import AppRouter from '../shared/components/layout/AppRouter';
import { useAuthStore } from '../features/auth/stores/authStore';
import { useResetErrorStateOnLogout } from '../shared/hooks/useResetErrorStateOnLogout';
import { ConstructionThemeProvider } from '../shared/components/theme/ThemeProvider';
import { queryClient } from '../shared/lib/queryClient';
import { PermissionsProviderAdapter } from './providers/PermissionsProviderAdapter';
import { FeaturesProviderAdapter } from './providers/FeaturesProviderAdapter';
import { DebugPanel } from '../shared/components/dev/DebugPanel';
import { FeatureSwitchGate, Features } from '@billda/features-ui';
import { PermissionGate, Permissions } from '@billda/permissions-ui';
import { CommandPaletteProvider } from '../features/search/providers/CommandPaletteProvider';
import { TopAppBar } from '../shared/components/layout/TopAppBar';
import { ConnectionErrorDialog } from '../shared/components/errors/ConnectionErrorDialog';
import { OrgChangedDialog } from '../shared/components/errors/OrgChangedDialog';
import { AppSnackbar } from '../shared/components/ui/AppSnackbar';
import { UploadTray } from '../features/sites/components/files/UploadTray';
import { GettingStartedFab } from '../features/onboarding/components/GettingStartedFab';
import { CompanyDetailsBanner } from '../features/organisation/components/CompanyDetailsBanner';
import { useAuthInitialization } from '../features/auth/hooks/useAuthInitialization';
import { useOrganizationSync } from '../features/organisation/hooks/useOrganizationSync';
import { ExpandedSidebar } from '../shared/components/layout/sidebar/ExpandedSidebar';
import { useSwipeToOpenSidebar } from '../shared/hooks/useSwipeToOpenSidebar';
import { useShellStore } from '../shared/stores/shellStore';
import { DARK_SHELL_SURFACE, LIGHT_SHELL_SURFACE } from '@billda/shared-ui';

const AppContentInner: React.FC = () => {
  const isAuthReady = useAuthInitialization();
  useResetErrorStateOnLogout();
  useOrganizationSync();

  const { isAuthenticated } = useAuthStore();
  const sidebarCollapsed = useShellStore(state => state.sidebarCollapsed);
  const location = useLocation();
  const swipeRef = useSwipeToOpenSidebar();

  // Check if we're in the invitation flow or download app page (should hide navigation even when authenticated)
  const isInvitationFlow = location.pathname.startsWith('/invitation/');
  const isDownloadAppPage = location.pathname === '/download-app';
  const isSettingsPage = location.pathname.startsWith('/settings');
  const shouldShowNavigation =
    isAuthenticated &&
    !isInvitationFlow &&
    !isDownloadAppPage &&
    !isSettingsPage;

  // Handle unhandled promise rejections - must be called before any early returns
  useEffect(() => {
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      console.error('Unhandled promise rejection:', event.reason);
      // Prevent the default behavior (console error)
      event.preventDefault();
    };

    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener(
        'unhandledrejection',
        handleUnhandledRejection
      );
    };
  }, []);

  // Show loading screen while checking authentication
  if (!isAuthReady) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          width: '100vw',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Box ref={swipeRef} sx={{ display: 'flex' }}>
        {shouldShowNavigation && <ExpandedSidebar />}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            ml: 0,
            height: '100vh',
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: shouldShowNavigation
              ? theme =>
                  theme.palette.mode === 'dark'
                    ? DARK_SHELL_SURFACE
                    : LIGHT_SHELL_SURFACE
              : 'transparent',
          }}
        >
          {/* Top App Bar */}
          {shouldShowNavigation && <TopAppBar />}

          {/* Global company details banner */}
          {shouldShowNavigation && <CompanyDetailsBanner />}

          {/* Main Content */}
          <Box
            sx={{
              flexGrow: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                borderRadius:
                  shouldShowNavigation && !sidebarCollapsed
                    ? theme => ({
                        xs: 0,
                        md: `${Number(theme.shape.borderRadius) * 3}px 0 0 0`,
                      })
                    : 0,
                bgcolor: shouldShowNavigation
                  ? 'background.default'
                  : 'transparent',
                borderTop: shouldShowNavigation
                  ? theme =>
                      theme.palette.mode === 'dark'
                        ? '1px solid rgba(255, 255, 255, 0.08)'
                        : '1px solid rgba(0, 0, 0, 0.08)'
                  : 'none',
                overflow: 'auto',
              }}
            >
              <AppRouter />
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};

const AppContent: React.FC = () => {
  return (
    <AppErrorBoundary>
      <ConstructionThemeProvider>
        <PermissionsProviderAdapter>
          <FeaturesProviderAdapter>
            <BrowserRouter>
              <CommandPaletteProvider>
                <AppContentInner />
              </CommandPaletteProvider>
              <GettingStartedFab />
              <ConnectionErrorDialog />
              <OrgChangedDialog />
              <AppSnackbar />
              <FeatureSwitchGate feature={Features.Files.IsEnabled}>
                <UploadTray />
              </FeatureSwitchGate>
            </BrowserRouter>
            <PermissionGate
              permission={Permissions.Developer.Access}
              showRequiredPermissions={false}
            >
              <FeatureSwitchGate feature={Features.Debug.IsPanelEnabled}>
                <DebugPanel />
              </FeatureSwitchGate>
            </PermissionGate>
          </FeaturesProviderAdapter>
        </PermissionsProviderAdapter>
      </ConstructionThemeProvider>
    </AppErrorBoundary>
  );
};

const App: React.FC = () => {
  // Remove HTML loading screen when React is ready
  useEffect(() => {
    const loadingScreen = document.getElementById('app-loading-screen');
    if (loadingScreen) {
      // Add fade-out class for smooth transition
      loadingScreen.classList.add('fade-out');
      // Remove from DOM after animation completes
      setTimeout(() => loadingScreen.remove(), 300);
    }
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <AppContent />
    </QueryClientProvider>
  );
};

export default App;
